/**
 * Application wiring for the error-reporting pipeline.
 *
 * This is the one place that couples the reporting pipeline to app state. The
 * core pipeline (capture, build, queue) imports no stores, so it stays
 * lightweight and runnable even when the app graph is broken; here we register
 * providers that read live Zustand state, then install the global handlers.
 * (`ErrorFallback`, a React component rendered only inside a working React
 * tree, reads auth state directly — that's fine, it can't run when React is
 * down anyway.)
 *
 * Call once from the entry point.
 */

import {
  installDomGuard,
  installGlobalErrorHandlers,
  setErrorContextProvider,
  setReportUsernameProvider,
} from "@/components/error-boundary";
import { useAuthState } from "@/zustand/AuthState";
import { useConnectionState } from "@/zustand/ConnectionState";
import { useNavigationState } from "@/zustand/NavigationState";

export function initErrorReporting(): void {
  // First: patch the DOM primitives before React ever renders, so a page
  // translator that has already rewritten the shell cannot crash the first
  // unmount. Reports it emits still flow through the providers registered
  // below (they are read lazily, at capture time).
  installDomGuard();

  setErrorContextProvider(() => {
    const connection = useConnectionState.getState();
    return {
      // Boolean only — never the token itself.
      isAuthenticated: Boolean(useAuthState.getState().tk),
      wsConnectionStatus: connection.wsConnectionStatus,
      networkStatus: connection.networkStatus,
      activeChatId: useNavigationState.getState().activeChatId ?? null,
    };
  });

  setReportUsernameProvider(() => useAuthState.getState().username);

  installGlobalErrorHandlers();
}
